<script lang="ts" setup>
import { required } from '@vuelidate/validators';
import { useTemplateStore } from '@/store/templateStore';

interface Props {
  templateId: string;
}
const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const templateStore = useTemplateStore();
const loading = ref(false);
const message = useAlerts();
const oldTitle = computed(
  () =>
    templateStore.templateLink(props.templateId)?.title ||
    templateStore.template(props.templateId)?.title ||
    '',
);

const formData = ref({
  title: '',
});

const formRules = {
  title: [required],
};

async function saveTemplate() {
  loading.value = true;
  const { title } = formData.value;

  return await templateStore
    .copy({
      id: props.templateId,
      title,
    })
    .then(() => {
      message.success(
        $t('template.templateCopySuccess', {
          origininalTemplateTitle: oldTitle.value,
          copiedTemplateTitle: title,
        }),
      );
      emit('close');
    })
    .catch(message.error)
    .finally(() => {
      loading.value = false;
      emit('close');
    });
}
</script>

<template>
  <UiModal
    ref="modalRef"
    :title="$t('template.copyTemplate')"
    :button-abort-text="$t('button.cancel')"
    @close="emit('close')"
  >
    <UiForm
      :rules="formRules"
      :form-data="formData"
      @form-submit="saveTemplate"
    >
      <template #default="{ formErrors }">
        <UiInput
          v-model="formData.title"
          :label="$t('template.templateTitle')"
          :placeholder="
            $t('template.copyOfTemplate', {
              template: oldTitle,
            })
          "
          :errors="formErrors.title"
        />
      </template>
      <template #bottom="{ formSubmit, hasErrors }">
        <UiButtonRow
          :cancel-button-text="$t('button.cancel')"
          :action-button-text="$t('button.save')"
          :loading="loading"
          :disabled="hasErrors"
          expand
          @cancel="emit('close')"
          @action="formSubmit"
        />
      </template>
    </UiForm>
  </UiModal>
</template>
